import { CalendarCheck, CreditCard, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Columna de presentación de la pantalla de acceso.
 *
 * Es la gemela de la de sporttia-play, pero contada desde el otro lado del
 * mostrador: allí se reserva una pista, aquí se gestiona la instalación que
 * la alquila. Las tres capacidades van en el orden en que las usa un gestor
 * a lo largo del día —abrir la agenda, mover las clases, cuadrar la caja— y
 * son capacidades del panel, no cifras de marketing.
 *
 * Sobre la banda de acceso (`--auth`, #12150f) el texto va en
 * `auth-foreground` (18,4:1) y el secundario en `auth-muted` (8,0:1). Los
 * iconos son lima puro, que ahí da 12,9:1.
 */
const BENEFITS = [
    {
        icon: CalendarCheck,
        titleKey: 'login.benefitBookingTitle',
        textKey: 'login.benefitBookingText',
    },
    {
        icon: Users,
        titleKey: 'login.benefitActivitiesTitle',
        textKey: 'login.benefitActivitiesText',
    },
    {
        icon: CreditCard,
        titleKey: 'login.benefitBillingTitle',
        textKey: 'login.benefitBillingText',
    },
] as const;

type LoginAsideProps = {
    className?: string;
};

export default function LoginAside({ className = '' }: LoginAsideProps) {
    const { t } = useTranslation();

    return (
        <aside className={`space-y-8 ${className}`}>
            <ul className="space-y-5">
                {BENEFITS.map(({ icon: Icon, titleKey, textKey }) => (
                    <li key={titleKey} className="flex items-start gap-4">
                        <span className="bg-auth-foreground/10 text-brand flex size-10 shrink-0 items-center justify-center rounded-lg">
                            <Icon size={20} />
                        </span>
                        <div>
                            <p className="font-semibold">{t(titleKey)}</p>
                            <p className="text-auth-muted mt-1 text-sm">
                                {t(textKey)}
                            </p>
                        </div>
                    </li>
                ))}
            </ul>

            {/*
                Donde play ofrece la app del jugador, aquí va la puerta
                comercial: quien llega a este panel gestiona un centro, y
                mandarlo a descargar la app de reservas sería mandarlo al
                producto equivocado.
            */}
            <div className="border-auth-foreground/25 border-t pt-6">
                <p className="font-semibold">{t('login.notYetCustomer')}</p>
                <p className="text-auth-muted mt-1 text-sm">
                    {t('login.notYetCustomerText')}
                </p>
                <a
                    href="https://sporttia.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-auth-foreground border-auth-foreground/40 hover:bg-auth-foreground/10 mt-4 inline-flex h-10 items-center gap-2 rounded-lg border px-4 text-sm font-medium no-underline transition-colors"
                >
                    {t('login.requestInfo')}
                </a>
            </div>
        </aside>
    );
}
